import { captureLoaded } from '../../lib/funnels/functions/v1/metrics/captureLoaded'
import { handleDownsellClick } from '../../lib/funnels/functions/v1/default/handleDownsellClick'
import { shuffleArray } from '../../lib/funnels/functions/v1/default/shuffleArray'
import { redirectToNerdwallet } from '../../lib/funnels/functions/v1/middleware/redirectToNerdwallet'
import { redirectToSelf } from '../../lib/funnels/functions/v1/middleware/redirectToSelf'

import { fetchWithCache } from '../../lib/funnels/functions/v1/html/fetchWithCache'
import { parseEditorJsHtml } from '../../lib/funnels/functions/v1/html/parseEditorJsHtml'
import { getThanksParams } from '../../lib/funnels/functions/v1/html/getThanksParams'
import { getCookieValue } from '@turbodebt/turbo-shared'
import { redirectToDownsell } from '../../lib/funnels/functions/v1/middleware/redirectToDownsell'
import { getDailyOfferClicks } from '../../lib/funnels/functions/v1/middleware/getDailyOfferClicks'
import { runConcurrentPromises } from '../../lib/funnels/functions/v1/default/promiseHelper'
import { compareTimes } from '../../lib/funnels/functions/v1/default/compareTimes'
import { hasBlockedSubId } from '../../lib/funnels/functions/v1/default/hasBlockedSubId'
import { isOfferAvailable } from '../../lib/funnels/functions/v1/default/isOfferAvailable'

import './index.css'

// if the 'chatCompany' cookie is set, ping /api/downsell for potential redirect URL
const chatCompany = getCookieValue('chatCompany') || ''
const downsellFlagKey = 'downsell_redirect_checked'

if (chatCompany && !sessionStorage.getItem(downsellFlagKey)) {
  redirectToDownsell(chatCompany)
  sessionStorage.setItem(downsellFlagKey, '1')
}

document.addEventListener('DOMContentLoaded', async () => {
  captureLoaded()

  const OFFERS_API_URL =
    'https://blog.turbodevhub.com/api/listings?sort[0]=Position:asc&populate[BulletPoints][fields][0]=*&populate[Logo][fields][0]=*&populate[weekday_schedules]=*&fields[0]=*&publicationState=live&locale[0]=en'

  const strapiOffers = await fetchWithCache(
    OFFERS_API_URL,
    'td_offers',
    24 * 60 * 60, // (1 day)
  )

  // transform strapiOffers to pass to offerTemplate
  const transformedOffers = strapiOffers.data.map(
    (/** @type {StrapiOffers} */ item) => {
      const attr = item.attributes
      return {
        slug: attr.Slug,
        link: attr.Tracking_URL,
        name: attr.Name,
        image: attr.Logo?.data?.attributes?.url || '',
        title: attr.Headline,
        cta: attr.CTA,
        cta2: attr.Secondary_CTA,
        link2: attr.Secondary_URL,
        bullets: Array.isArray(attr.BulletPoints)
          ? attr.BulletPoints.map((bp) => bp.Point)
          : [],
        disclaimer: attr.Short_Disclaimer,
        disclaimerLong: parseEditorJsHtml(attr.Disclaimer),
        position: attr.Position,
        enabled: attr.Enabled,
        availableStates: attr.Available_States,
        restrictedStates: attr.Restricted_States,
        dailyClickCap: attr.Daily_Click_Cap,
        startTime: attr.Start_Time,
        endTime: attr.End_Time,
        blocked_subids: attr.blocked_subids,
        weekday_schedules: attr.weekday_schedules || [],
      }
    },
  )

  const {
    debtOwed,
    email,
    eventID,
    firstName,
    lastName,
    phone,
    state,
    zipcode,
    trksub,
    encodedSub1,
    clickId,
    sub1,
  } = getThanksParams()

  /**
   * Create offer HTML template
   * @param {ThanksOffers} offer
   * @returns {string}
   */
  const offerTemplate = (offer) => {
    const {
      slug,
      link,
      name,
      image,
      title,
      cta,
      cta2,
      link2,
      bullets,
      disclaimer,
      disclaimerLong,
      dailyClickCap,
    } = offer

    const offerBullets = bullets
      .map((bullet) => {
        return `<li>${bullet}</li>`
      })
      .join('')

    let action2 = ''
    if (link2 && cta2) {
      action2 = `<a href="${link2}">${cta2}</a>`
    }

    const replacedLink = link
      .replace(/\{email}/g, email || '')
      .replace(/\{first}/g, firstName || '')
      .replace(/\{last}/g, lastName || '')
      .replace(/\{phone}/g, phone || '')
      .replace(/\{state}/g, state || '')
      .replace(/\{trksub}/g, trksub || '')
      .replace(/\{zipcode}/g, zipcode || '')
      .replace(/\{clickId}/g, eventID || '')
      .replace(/\{encodedSub1}/g, encodedSub1 || '')

    return `
      <div class="offer-card" data-offer="${slug}" data-daily-cap="${dailyClickCap || 0}">
        <div class="img-container">
          <a href="${replacedLink}"><img alt="${name}" src="${image}" class="offer-logo" /></a>
          <span class="short-disclaimer">${disclaimer ? disclaimer : ''}</span>
        </div>
        <div class="content-container">
            <h3><a href="${replacedLink}">${name}</a></h3>
            <h4>${title}</h4>
            <ul>
              ${offerBullets}
            </ul>
            <div class="long-disclaimer">${disclaimerLong ? disclaimerLong : ''}</div>
            <span class="short-disclaimer-mobile">${disclaimer ? disclaimer : ''}</span>
        </div>
        <div class="action-container">
          <a href="${replacedLink}">${cta}</a>
          ${action2}
        </div>
      </div>
    `
  }

  const nwLeadData =
    phone && phone.length >= 10
      ? {
          firstName: firstName.length > 1 ? firstName : null,
          lastName: lastName.length > 1 ? lastName : null,
          phoneNumber: phone.startsWith('1') ? `+${phone}` : `+1${phone}`,
          email: email || null,
          personalLoanRequestAmount: debtOwed || 10000,
          clickId,
        }
      : null

  const selfLeadData =
    email && email.length > 5
      ? {
          first_name: firstName.length > 1 ? firstName : null,
          last_name: lastName.length > 1 ? lastName : null,
          email_address: email,
          phone_number: phone,
          tracking_id: clickId,
          loan_amount: debtOwed || 10000,
        }
      : null

  /**
   * Filter all offers and get list of offers with daily cap
   */
  const cappedOffers = transformedOffers.filter(
    (/** @type {{ dailyClickCap: number }} */ offer) => offer.dailyClickCap,
  )

  /**
   * Returns array of offers with current daily caps
   * @param {Array<ThanksOffers>} cappedOffersArray
   */
  async function getDailyOfferClicksMap(cappedOffersArray) {
    const slugs = cappedOffersArray.map((o) => o.slug).filter(Boolean)
    const counts = await runConcurrentPromises(
      slugs,
      (slug) => getDailyOfferClicks(slug),
      5,
    )
    return slugs.reduce((acc, slug, idx) => {
      acc[slug] = counts[idx] ?? 0
      return acc
    }, /** @type {Record<string, number>} */ ({}))
  }

  const dailyClickMap = await getDailyOfferClicksMap(cappedOffers)

  const offersContainer = document.getElementById('offers')
  if (!offersContainer) {
    throw Error('unexpected: offers container not found')
  }

  /**
   * Categorize offers into positioned & shuffleable arrays.
   * @param {Array<ListingOffers>} allOffers
   * @returns {{ positionedOffers: Array<ListingOffers>, lastOffers: Array<ListingOffers>, shuffleableOffers: Array<ListingOffers> }}
   */
  function categorizeOffers(allOffers) {
    /**
     * @type {Array<ListingOffers>}
     */
    const shuffleableOffers = []
    /**
     * @type {Array<ListingOffers>}
     */
    const positionedOffers = []
    /**
     * @type {Array<ListingOffers>}
     */
    const lastOffers = []

    allOffers.forEach((offer) => {
      if (offer.enabled === false) return

      if (offer.position === 0) {
        shuffleableOffers.push(offer)
      }

      if (offer.position > 0) {
        positionedOffers.push(offer)
      }

      if (offer.position < 0) {
        lastOffers.push(offer)
      }
    })

    positionedOffers.sort((a, b) => a.position - b.position)
    lastOffers.sort((a, b) => a.position - b.position)

    return { shuffleableOffers, positionedOffers, lastOffers }
  }

  const { shuffleableOffers, positionedOffers, lastOffers } =
    categorizeOffers(transformedOffers)

  const offersShuffled = shuffleArray(shuffleableOffers)
  const finalOffers = [...positionedOffers, ...offersShuffled, ...lastOffers]

  offersContainer.innerHTML = ''

  const today = new Date()
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase()

  finalOffers.forEach((o) => {
    const offer = /** @type {ListingOffers} */ (o)

    if (offer.dailyClickCap && offer.slug) {
      const currentCount = dailyClickMap[offer.slug] ?? 0
      if (currentCount >= offer.dailyClickCap) {
        return
      }
    }

    if (offer.startTime && offer.endTime) {
      const showOffer = compareTimes(offer.startTime, offer.endTime)
      if (!showOffer) {
        return
      }
    }

    if (
      Array.isArray(offer.availableStates) &&
      offer.availableStates.length > 0 &&
      !offer.availableStates.includes(state)
    ) {
      return
    }

    if (
      Array.isArray(offer.restrictedStates) &&
      offer.restrictedStates.length > 0 &&
      offer.restrictedStates.includes(state)
    ) {
      return
    }

    if (offer.blocked_subids && hasBlockedSubId(offer, [sub1])) {
      return
    }

    if (!isOfferAvailable(offer.weekday_schedules, today)) {
      return
    }

    offersContainer.innerHTML += offerTemplate(offer)
  })

  const links = offersContainer.querySelectorAll('.offer-card a')
  links.forEach((link) => {
    const href = link?.getAttribute('href')
    const slug = link?.closest('.offer-card')?.getAttribute('data-offer')
    const dailyCap = link
      ?.closest('.offer-card')
      ?.getAttribute('data-daily-cap')
    const hasDailyCap = dailyCap ? parseInt(dailyCap, 10) > 0 : false

    if (!slug || !href) {
      throw Error('no valid slug/href, events not attached')
    }

    link.addEventListener('click', async (e) => {
      if (hasDailyCap) {
        e.preventDefault()
        try {
          await handleDownsellClick(slug, 'downsell_offer_click', true)
        } catch (err) {
          console.error('failed to record downsell click:', err)
        }
        window.location.href = href
        return
      }

      // eslint-disable-next-line no-void
      void handleDownsellClick(slug, 'downsell_offer_click', false)
    })

    if (slug === 'nerdwallet') {
      link.addEventListener('click', async (e) => {
        e.preventDefault()
        await redirectToNerdwallet(nwLeadData, href, 'td-direct-list')
      })
    }
    if (slug === 'self') {
      link.addEventListener('click', async (e) => {
        e.preventDefault()
        await redirectToSelf(selfLeadData, href)
      })
    }
  })
})
